/**
 * The scrubber's window for one record (ui/DESIGN.md §3.4): its own span, from a little before its first document
 * to now, and a histogram of the record's own documents over it, so the track zooms to the journey instead of
 * showing a one-day order as a sliver at the end of thirty days. Pure, so `node --test src/stage/track.test.ts` runs it.
 */
import type { HistogramBucket } from '../lib/api.ts';

/** The window never gets shorter than this, so a record that just came still has a track to drag on. */
const MIN_SPAN = 3_600_000;
/** Buckets across the record's span. */
const BUCKETS = 96;

/** Where a single record's window starts: its first document minus a tenth of the time since, at least an hour back. */
export function spanStart(first: number, now: number): number {
  const since = Math.max(0, now - first);
  return Math.min(first - since / 10, now - MIN_SPAN);
}

/** "13 h", "16 d", "45 min": how long the record's own span is, from its first document to now, in round figures. */
export function spanLabel(first: number, now: number): string {
  const ms = Math.max(0, now - first);
  if (ms >= 2 * 86_400_000) return `${Math.round(ms / 86_400_000)} d`;
  if (ms >= 3_600_000) return `${Math.round(ms / 3_600_000)} h`;
  return `${Math.max(1, Math.round(ms / 60_000))} min`;
}

/** The record's own moments (its documents, its steps, its runs) as a histogram over the window, with its bucket as "<n>s". */
export function eventHistogram(times: number[], start: number, now: number): { histogram: HistogramBucket[]; interval: string } {
  const span = Math.max(1, now - start);
  const bucket = Math.max(1000, Math.round(span / BUCKETS / 1000) * 1000);
  const counts = new Map<number, number>();
  for (const t of times) {
    if (t < start || t > now) continue;
    const k = Math.floor((t - start) / bucket);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const histogram = [...counts.entries()].sort((a, b) => a[0] - b[0]).map(([k, count]) => ({ time: start + k * bucket, count }));
  return { histogram, interval: `${bucket / 1000}s` };
}
